import imageCompression from 'browser-image-compression'

/**
 * Image compression options
 */
export interface CompressionOptions {
  /** Maximum file size in MB (default: 1) */
  maxSizeMB?: number
  /** Maximum width or height in pixels (default: 2048) */
  maxWidthOrHeight?: number
  /** Output file type (default: 'image/webp') */
  fileType?: 'image/webp' | 'image/jpeg' | 'image/png'
  /** Use web worker for non-blocking compression (default: true) */
  useWebWorker?: boolean
}

/**
 * Result of image compression
 */
export interface CompressionResult {
  /** Compressed file */
  file: File
  /** Width of the image in pixels */
  width: number
  /** Height of the image in pixels */
  height: number
  /** Original file size in bytes */
  originalSize: number
  /** Compressed file size in bytes */
  compressedSize: number
  /** MIME type of the compressed file */
  mimeType: string
}

/**
 * Default compression settings optimized for web delivery
 */
const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxSizeMB: 1,
  maxWidthOrHeight: 2048,
  fileType: 'image/webp',
  useWebWorker: true,
}

/**
 * Get the dimensions of an image file
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

/**
 * Compress an image file and return metadata
 *
 * Uses browser-image-compression to:
 * - Reduce file size to target (default 1MB)
 * - Resize to max dimension (default 2048px)
 * - Convert to WebP format for better compression
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed file with metadata
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const originalSize = file.size

  // Compress the image
  const compressedBlob = await imageCompression(file, {
    maxSizeMB: opts.maxSizeMB,
    maxWidthOrHeight: opts.maxWidthOrHeight,
    fileType: opts.fileType,
    useWebWorker: opts.useWebWorker,
    // Preserve EXIF orientation but strip other metadata
    preserveExif: false,
  })

  // Create a new File from the compressed blob with WebP extension
  const extension = opts.fileType === 'image/webp' ? '.webp' : opts.fileType === 'image/jpeg' ? '.jpg' : '.png'
  const baseName = file.name.replace(/\.[^/.]+$/, '')
  const compressedFile = new File([compressedBlob], `${baseName}${extension}`, {
    type: opts.fileType,
  })

  // Get dimensions of the compressed image
  const dimensions = await getImageDimensions(compressedFile)

  return {
    file: compressedFile,
    width: dimensions.width,
    height: dimensions.height,
    originalSize,
    compressedSize: compressedFile.size,
    mimeType: opts.fileType,
  }
}

/**
 * Format bytes as a human-readable string
 *
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Calculate compression ratio as a percentage saved
 *
 * @param originalSize - Original file size in bytes
 * @param compressedSize - Compressed file size in bytes
 * @returns Percentage saved (e.g., "73%")
 */
export function getCompressionRatio(originalSize: number, compressedSize: number): string {
  if (originalSize === 0) return '0%'
  const saved = ((originalSize - compressedSize) / originalSize) * 100
  return `${Math.round(saved)}%`
}

/**
 * Check if a file is an image based on MIME type
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

/**
 * Check if compression is needed based on file size
 *
 * @param file - The file to check
 * @param maxSizeMB - Maximum size in MB before compression is needed
 * @returns true if the file should be compressed
 */
export function needsCompression(file: File, maxSizeMB: number = 1): boolean {
  return file.size > maxSizeMB * 1024 * 1024
}
